// src/pages/team/AnnouncementsPage.js
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db, auth } from "../../firebase";
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc,
  orderBy 
} from "firebase/firestore";

function AnnouncementsPage() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isManager, setIsManager] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchTeamAndAnnouncements = async () => {
      try {
        // Get team data
        console.log("Fetching team with ID:", teamId);
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          console.log("Team data retrieved:", teamData.name);
          setTeam(teamData);
  
          // Check if current user is a coach/manager or admin
          const currentUser = auth.currentUser;
          if (currentUser) {
            console.log("Current user ID:", currentUser.uid);
            console.log("Team coach ID:", teamData.coachId);
            console.log("Team admins:", teamData.admins);
            
            // Check if user is coach or in the admins array
            if (
              currentUser.uid === teamData.coachId || 
              (teamData.admins && teamData.admins.includes(currentUser.uid))
            ) {
              console.log("User has manager permissions");
              setIsManager(true);
            }
          }
  
          try {
            // Get announcements with proper ordering
            console.log("Attempting to fetch announcements for team:", teamId);
            const announcementsQuery = query(
              collection(db, "announcements"),
              where("teamId", "==", teamId)
              // Temporarily remove the orderBy to simplify debugging
              // orderBy("announcementDate", "desc")
            );
            
            const announcementsSnap = await getDocs(announcementsQuery);
            console.log("Announcements fetched:", announcementsSnap.docs.length);
            
            setAnnouncements(announcementsSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            })));
          } catch (announcementError) {
            console.error("Specific error fetching announcements:", announcementError);
            console.error("Error code:", announcementError.code);
            console.error("Error message:", announcementError.message);
            // Continue execution even if announcements fail to load
          }
        } else {
          console.error("Team not found with ID:", teamId);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        console.error("Error details:", JSON.stringify(err));
      }
      setLoading(false);
    };
  
    fetchTeamAndAnnouncements();
  }, [teamId]);

  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      await deleteDoc(doc(db, "announcements", announcementId));
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    
    const date = new Date(timestamp.seconds * 1000);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'long' }) + 
        ` at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  };

  // Sort announcements based on current sort order
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    const dateA = a.announcementDate?.seconds || a.createdAt?.seconds || 0;
    const dateB = b.announcementDate?.seconds || b.createdAt?.seconds || 0;
    
    return sortOrder === "newest" 
      ? dateB - dateA 
      : dateA - dateB;
  });

  // Filter announcements based on search query
  const filteredAnnouncements = sortedAnnouncements.filter(announcement => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      announcement.title?.toLowerCase().includes(query) || 
      announcement.content?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p style={styles.loadingText}>Loading announcements...</p>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Team Announcements</h1>
            {team && <p style={styles.teamName}>{team.name}</p>}
          </div>
          
          {announcements.length > 0 && (
            <div style={styles.countBadge}>
              {announcements.length} announcement{announcements.length !== 1 && 's'}
            </div>
          )}
        </div>

        {/* Toolbar */}
        {announcements.length > 0 && (
          <div style={styles.toolbar}>
            <div style={styles.searchContainer}>
              <div style={styles.searchIcon}>🔍</div>
              <input 
                type="text" 
                placeholder="Search announcements..." 
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  style={styles.clearSearchButton}
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>
            
            <div style={styles.sortContainer}>
              <label style={styles.sortLabel}>Sort by: </label>
              <select 
                style={styles.sortSelect}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
          </div>
        )}

        {/* New announcement button for managers */}
        {isManager && (
          <Link to={`/team/${teamId}/dashboard/announcements/add`} style={styles.newAnnouncementLink}>
            <div style={styles.newAnnouncementButton}>
              <span style={styles.plusIcon}>+</span> Create Announcement
            </div>
          </Link>
        )}

        {/* Announcements content */}
        {announcements.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIconContainer}>
              <span style={styles.emptyIcon}>📢</span>
            </div>
            <h3 style={styles.emptyText}>No announcements yet</h3>
            <div style={styles.emptyDivider} />
            <p style={styles.emptySubtext}>
              {isManager 
                ? "Create your first announcement to keep your team informed!"
                : "Check back later for updates from your coach"}
            </p>
            {isManager && (
              <Link to={`/team/${teamId}/announcements/new`} style={styles.emptyActionLink}>
                <div style={styles.emptyActionButton}>
                  Create Your First Announcement
                </div>
              </Link>
            )}
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div style={styles.noResults}>
            <p>No announcements match your search for "{searchQuery}"</p>
            <button 
              onClick={() => setSearchQuery("")}
              style={styles.clearSearchLink}
            >
              Clear search
            </button>
          </div>
        ) : (
          <div style={styles.grid}>
            {filteredAnnouncements.map(announcement => (
              <div key={announcement.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>{announcement.title}</h3>
                  
                  {isManager && (
                    <div style={styles.cardActions}>
                      <Link 
                        to={`/team/${teamId}/announcements/edit/${announcement.id}`}
                        style={styles.editButton}
                      >
                        ✎
                      </Link>
                      
                      {deleteConfirm === announcement.id ? (
                        <div style={styles.confirmDelete}>
                          <button 
                            style={styles.confirmButton}
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                          >
                            Yes
                          </button>
                          <button 
                            style={styles.cancelButton}
                            onClick={() => setDeleteConfirm(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          style={styles.deleteButton}
                          onClick={() => setDeleteConfirm(announcement.id)}
                          aria-label="Delete announcement"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                <div style={styles.cardContent}>
                  {announcement.content.split('\n').map((paragraph, idx) => (
                    <p key={idx} style={styles.paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </div>
                
                <div style={styles.cardFooter}>
                  <time style={styles.timestamp}>
                    {formatDate(announcement.announcementDate || announcement.createdAt)}
                  </time>
                  
                  {announcement.important && (
                    <div style={styles.importantTag}>Important</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: '#f3f4f6',
    minHeight: '100vh',
    padding: '2rem 1rem',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '2rem',
    fontFamily: 'Inter, system-ui, sans-serif',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },
  teamName: {
    fontSize: '1.125rem',
    color: '#6b7280',
    margin: '0.25rem 0 0 0',
  },
  countBadge: {
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    padding: '0.5rem 1rem',
    borderRadius: '999px',
    fontSize: '0.875rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchContainer: {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    maxWidth: '400px',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    fontSize: '0.875rem',
    color: '#9ca3af',
  },
  searchInput: {
    flex: '1',
    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '0.875rem',
    width: '100%',
  },
  clearSearchButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '0 0.75rem',
    fontSize: '0.875rem',
  },
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  sortLabel: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginRight: '0.5rem',
  },
  sortSelect: {
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    fontSize: '0.875rem',
    color: '#374151',
    cursor: 'pointer',
  },
  newAnnouncementLink: {
    textDecoration: 'none',
    display: 'block',
    marginBottom: '2rem',
  },
  newAnnouncementButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0d9488',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  plusIcon: {
    marginRight: '0.5rem',
    fontSize: '1.25rem',
  },
  grid: {
    display: 'grid',
    gap: '1.5rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e5e7eb',
    position: 'relative',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
    flex: 1,
  },
  cardActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    textDecoration: 'none',
  },
  deleteButton: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    border: 'none',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmDelete: {
    display: 'flex',
    gap: '0.25rem',
  },
  confirmButton: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  cancelButton: {
    backgroundColor: '#9ca3af',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  cardContent: {
    fontSize: '1rem',
    color: '#4b5563',
    lineHeight: '1.6',
    marginBottom: '1.5rem',
  },
  paragraph: {
    margin: '0 0 0.75rem 0',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  timestamp: {
    fontSize: '0.875rem',
    color: '#6b7280',
    display: 'block',
  },
  importantTag: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    padding: '0.25rem 0.75rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 2rem',
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    border: '2px dashed #e5e7eb',
  },
  emptyIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1rem',
  },
  emptyIcon: {
    fontSize: '3rem',
  },
  emptyText: {
    fontSize: '1.5rem',
    color: '#4b5563',
    fontWeight: '600',
    margin: '0',
  },
  emptySubtext: {
    fontSize: '1rem',
    color: '#6b7280',
    margin: '0.5rem 0 1.5rem',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  emptyDivider: {
    height: '2px',
    backgroundColor: '#e5e7eb',
    width: '60px',
    margin: '1rem auto',
  },
  emptyActionLink: {
    textDecoration: 'none',
  },
  emptyActionButton: {
    display: 'inline-block',
    backgroundColor: '#0d9488',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  noResults: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#6b7280',
  },
  clearSearchLink: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#0369a1',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '0.875rem',
    padding: '0.5rem',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderRadius: '50%',
    borderTop: '4px solid #0d9488',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '1rem',
    color: '#6b7280',
    fontSize: '1rem',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export default AnnouncementsPage;